import React, { useMemo, useState } from "react";
import {
  Table,
  TableCell,
  tableCellTypesEnum,
  CheckBox,
  CheckBoxVariant,
} from "@SeemplicityDev/shared";
import { type CellContext, createColumnHelper } from "@tanstack/react-table";
import { buildDashboardNamePath } from "../../CustomizedDashboard/dashboardUrl";
import { Link } from "react-router-dom";
import Tabs, { type TabsMenuItem } from "../../Components/Tabs/Tabs";
import QueueStatus from "../../RemediationQueues/RemediationQueuesList/QueueStatus/QueueStatus";
import { queueStatus } from "../../RemediationQueues/RemediationQueuesUtils";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../General/useSeverityAsPriority";
import { RULE_KIND_TO_PAGE_LINK } from "../../Rules/ruleTypes/types";
import {
  ANALYST_RUNS_PATH,
  buildAnalystDetailsPanePath,
} from "../../AnalystRuns/analystRunsRoutes";
import type {
  DependencySet,
  DependencyAiAnalyst,
  DependencyAutomation,
  DependencyRule,
  DependencyWidget,
  RuleKind,
} from "./types";
import HiddenWidgetsBanner from "./HiddenWidgetsBanner";
import "./DependenciesModal.scss";

const TAB = {
  rules: "rules",
  automations: "automations",
  aiAnalysts: "ai_analysts",
  widgets: "widgets",
} as const;

const ANALYST_STATE_ENABLED = "ENABLED";

// Table geometry, so the table area is sized to the tallest tab up front and
// switching tabs never changes the modal's height. Header/frame are measured
// from the shared Table; the cap keeps long lists scrolling instead of growing.
const TABLE_ROW_HEIGHT = 44;
const TABLE_HEADER_HEIGHT = 51;
// Top/bottom borders plus the sub-pixel the browser adds when rounding rows.
const TABLE_FRAME_HEIGHT = 3;
const TABLE_MAX_HEIGHT = 284;

type TabId = (typeof TAB)[keyof typeof TAB];

// entity_id is the analyst's id in the analyst service; rows without one link to the list page.
const buildAnalystDependencyPath = (analyst: DependencyAiAnalyst): string => {
  if (typeof analyst.entity_id === "number") {
    return buildAnalystDetailsPanePath(String(analyst.entity_id));
  }
  return ANALYST_RUNS_PATH;
};

const RULE_KIND_TAB = new Map<RuleKind, string>(
  Object.entries(RULE_KIND_TO_PAGE_LINK) as [RuleKind, string][]
);

export interface DependenciesModalProps {
  // Name of the entity being deleted/edited (filter or scope).
  entityName?: string;
  dependencies: DependencySet;
  // "delete" → blocker copy (can't delete); "edit" → save-as-new / update-everywhere copy.
  variant?: "delete" | "edit";
  entityLabel?: "filter" | "scope";
}

const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) {
    return items[0] ?? "";
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const DependenciesModal: React.FC<DependenciesModalProps> = ({
  entityName,
  dependencies,
  variant = "delete",
  entityLabel = "filter",
}) => {
  const { severityDisplay, getSeverityField } = useSeverityAsPriority();
  const isEdit = variant === "edit";
  const hiddenWidgetsCount = dependencies.hidden_widgets_count ?? 0;
  const widgetsCount = dependencies.widgets.length + hiddenWidgetsCount;

  // The copy names only the entity types that actually hold dependencies,
  // e.g. "rules and widgets" or "rules, automations, and widgets".
  const usedEntityTypes = joinWithAnd(
    [
      dependencies.rules.length ? "rules" : null,
      dependencies.automations.length ? "automations" : null,
      dependencies.ai_analysts.length ? "AI analysts" : null,
      dependencies.widgets.length ? "widgets" : null,
    ].filter((label): label is string => !!label)
  );

  // Only surface tabs that actually have dependencies — no empty "Rule (0)" tabs.
  const menuItems: TabsMenuItem[] = [
    ...(dependencies.rules.length
      ? [{ pageLink: TAB.rules, title: `Rule (${dependencies.rules.length})` }]
      : []),
    ...(dependencies.automations.length
      ? [
          {
            pageLink: TAB.automations,
            title: `Automation (${dependencies.automations.length})`,
          },
        ]
      : []),
    ...(dependencies.ai_analysts.length
      ? [
          {
            pageLink: TAB.aiAnalysts,
            title: `AI Analysts (${dependencies.ai_analysts.length})`,
          },
        ]
      : []),
    ...(widgetsCount
      ? [
          {
            pageLink: TAB.widgets,
            title: `Dashboard Widgets (${widgetsCount})`,
          },
        ]
      : []),
  ];

  // Open on the first rendered tab — the modal only mounts when at least one exists.
  const [activeTab, setActiveTab] = useState<TabId>(
    (menuItems[0]?.pageLink as TabId) ?? TAB.rules
  );

  const ruleColumns = useMemo(() => {
    const ch = createColumnHelper<DependencyRule>();
    return [
      ch.accessor("name", {
        id: "name",
        header: "Rule Name",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (p: CellContext<DependencyRule, string | null>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Link
              className={"dependency-link"}
              title={p.getValue() ?? undefined}
              to={`/policies/findings-rules/${RULE_KIND_TAB.get(p.row.original.kind) ?? "status"}`}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              {p.getValue() ?? "—"}
            </Link>
          </TableCell>
        ),
      }),
      ch.accessor("enabled", {
        id: "status",
        header: "Status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (p: CellContext<DependencyRule, boolean | null>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <CheckBox
              variant={CheckBoxVariant.TOGGLE}
              checked={!!p.getValue()}
              disabled
              onChange={() => undefined}
            />
          </TableCell>
        ),
      }),
    ];
  }, []);

  const automationColumns = useMemo(() => {
    const ch = createColumnHelper<DependencyAutomation>();
    return [
      ch.accessor("name", {
        id: "name",
        header: "Automation Name",
        enableSorting: false,
        enableColumnFilter: false,
        // Name gets twice the width of Status (2:1).
        size: 300,
        cell: (p: CellContext<DependencyAutomation, string | null>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Link
              className={"dependency-link"}
              title={p.getValue() ?? undefined}
              to={`/remediationQueue/${p.row.original.id}`}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              {p.getValue() ?? "—"}
            </Link>
          </TableCell>
        ),
      }),
      ch.accessor("status", {
        id: "status",
        header: "Status",
        enableSorting: false,
        enableColumnFilter: false,
        size: 150,
        cell: (p: CellContext<DependencyAutomation, string | null>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <QueueStatus
              status={
                queueStatus[p.getValue() as keyof typeof queueStatus]?.text ??
                p.getValue() ??
                ""
              }
              originalRow={p.row.original}
            />
          </TableCell>
        ),
      }),
    ];
  }, []);

  const aiAnalystColumns = useMemo(() => {
    const ch = createColumnHelper<DependencyAiAnalyst>();
    return [
      ch.accessor("name", {
        id: "name",
        header: "AI Analyst Name",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (p: CellContext<DependencyAiAnalyst, string | null>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Link
              className={"dependency-link"}
              title={p.getValue() ?? undefined}
              to={buildAnalystDependencyPath(p.row.original)}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              {p.getValue() ?? "—"}
            </Link>
          </TableCell>
        ),
      }),
      ch.accessor("state", {
        id: "state",
        header: "Status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (p: CellContext<DependencyAiAnalyst, string | null>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <CheckBox
              variant={CheckBoxVariant.TOGGLE}
              checked={p.getValue() === ANALYST_STATE_ENABLED}
              disabled
              onChange={() => undefined}
            />
          </TableCell>
        ),
      }),
    ];
  }, []);

  const widgetColumns = useMemo(() => {
    // Widget names carry severity/priority template tokens ({severity_field_name},
    // {severity_critical_field_name}, {severity_high_field_name}); resolve them to the
    // tenant's labels the same way InsightWidget does. Names without tokens pass through.
    const formatWidgetName = (name: string | null): string =>
      name
        ? name
            .replace("{severity_field_name}", severityDisplay)
            .replace(
              "{severity_critical_field_name}",
              getSeverityField(3, SeverityFieldsEnum.NAME)
            )
            .replace(
              "{severity_high_field_name}",
              getSeverityField(2, SeverityFieldsEnum.NAME)
            )
        : "—";
    const ch = createColumnHelper<DependencyWidget>();
    return [
      ch.accessor("name", {
        id: "name",
        header: "Dashboard Widgets",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (p: CellContext<DependencyWidget, string | null>) => (
          <TableCell>
            <span
              className={"dependency-name"}
              title={formatWidgetName(p.getValue())}
            >
              {formatWidgetName(p.getValue())}
            </span>
          </TableCell>
        ),
      }),
      ch.accessor("dashboard_name", {
        id: "dashboard",
        header: "Dashboard Name",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (p: CellContext<DependencyWidget, string | null>) => {
          const dashboardName = p.getValue();
          return (
            <TableCell type={tableCellTypesEnum.COMPONENT}>
              {dashboardName ? (
                <Link
                  className={"dependency-link"}
                  title={dashboardName}
                  to={buildDashboardNamePath(dashboardName)}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  {dashboardName}
                </Link>
              ) : (
                "—"
              )}
            </TableCell>
          );
        },
      }),
    ];
  }, [severityDisplay, getSeverityField]);

  const activeTable = useMemo(() => {
    switch (activeTab) {
      case TAB.automations:
        return { columns: automationColumns, data: dependencies.automations };
      case TAB.aiAnalysts:
        return { columns: aiAnalystColumns, data: dependencies.ai_analysts };
      case TAB.widgets:
        return { columns: widgetColumns, data: dependencies.widgets };
      case TAB.rules:
      default:
        return { columns: ruleColumns, data: dependencies.rules };
    }
  }, [
    activeTab,
    dependencies,
    ruleColumns,
    automationColumns,
    aiAnalystColumns,
    widgetColumns,
  ]);

  const tallestTabRows = Math.max(
    dependencies.rules.length,
    dependencies.automations.length,
    dependencies.ai_analysts.length,
    dependencies.widgets.length
  );
  const tableMinHeight = Math.min(
    TABLE_MAX_HEIGHT,
    TABLE_HEADER_HEIGHT + TABLE_FRAME_HEIGHT + TABLE_ROW_HEIGHT * tallestTabRows
  );

  return (
    <div className={"modal-text dependencies-modal"}>
      <div className={"dependencies-blocker-title"}>
        <span className={"modal-live-text"}>{entityName}</span>
        {isEdit
          ? ` is used by ${usedEntityTypes}.`
          : " is in use and cannot be deleted."}
      </div>
      <div className={"margin-bottom-20"}>
        {isEdit
          ? "Save as a new filter or update to apply changes everywhere."
          : `Remove it from all ${usedEntityTypes} below first.`}
      </div>

      <Tabs
        menuItems={menuItems}
        activeId={activeTab}
        onSelectTab={(item) =>
          item.pageLink && setActiveTab(item.pageLink as TabId)
        }
      />

      {activeTab === TAB.widgets && hiddenWidgetsCount > 0 && (
        <HiddenWidgetsBanner
          widgetsCount={hiddenWidgetsCount}
          dashboardsCount={dependencies.hidden_dashboards_count ?? 0}
          entityLabel={entityLabel}
        />
      )}

      <div
        className={"margin-top-20 dependencies-table"}
        style={{ minHeight: tableMinHeight, maxHeight: TABLE_MAX_HEIGHT }}
        data-testid={"dependencies-table"}
      >
        <Table
          rowHeight={TABLE_ROW_HEIGHT}
          columns={activeTable.columns}
          data={activeTable.data}
        />
      </div>
    </div>
  );
};

export default DependenciesModal;
